import {IComponentPanelObject} from "../ComponentPanel.js";
import {ITemplateModel} from "../../Model/Template/ITemplateModel.js";
import {KendoToolBarHandler} from "../../Tools/Misc.js";
import {String} from "../../Tools/Misc.js"
import {DirtyTracker, MakeDirty} from "../../Tools/MakeDirty.js";
import {Delegate} from "../../Tools/Delegate.js";
import {TemplateUpsert} from "../../Controller/Template/TemplateUpsert.js";
import {TemplateTranslate} from "../../Controller/Template/TemplateTranslate.js";
import {OnTemplateGroupSaved} from "../../Delegates/TemplateDelegate.js";
import {RessourceSlot} from "../../Tools/Disposable/RessourceSlot";
import {TemplateGeneraliteEditor} from "./TemplateGeneraliteEditor";
import {DisposableStack} from "../../Tools/Disposable/DisposableStack";
import {MonacoEditorComponent} from "../MonacoEditorComponent";
import {TemplatePreview} from "./TemplatePreview";
import {TemplateDeleteController} from "../../Controller/Template/TemplateDelete";
import {TemplateSujetEditor} from "./TemplateSujetEditor";
import {NotificationHelper} from "../../Helper/NotificationHelper";
import {TemplateContractEditor} from "./TemplateContractEditor";
import {IEasymailPackage} from "../../Model/Package/IEasymailPackage.js";
import {PackageImportDialog} from "../Package/PackageImportDialog.js";
import {PackageExportDialog} from "../Package/PackageExportDialog.js";
import {PackageMapper} from "../../Tools/Package/PackageMapper.js";

export class TemplateEditor implements IComponentPanelObject {
    #dirtyTrack: DirtyTracker<ITemplateModel>
    public MainContainer: JQuery | undefined;
    #jqContainer: JQuery | undefined;
    #jqToolbarContainer: JQuery | undefined;
    #jqLangBar: JQuery | undefined;
    #tabDivs = new Map<string, JQuery>();
    #kTabs: kendo.ui.TabStrip | undefined;
    #jqCodeTabItem: JQuery | undefined;
    #typeEditor: "plaintext" | "html" | "json" = "plaintext";

    #componentStack = new DisposableStack()
    #generaliteSlot = new RessourceSlot<TemplateGeneraliteEditor>();
    #codeSlot = new RessourceSlot<MonacoEditorComponent>();
    #modelSlot = new RessourceSlot<MonacoEditorComponent>();
    #previewSlot = new RessourceSlot<TemplatePreview>();
    #sujetSlot = new RessourceSlot<TemplateSujetEditor>();
    #contractSlot = new RessourceSlot<TemplateContractEditor>();
    // Onglet "Notifications" conservé en HTML (tabDivs.notifications) mais
    // sans contenu : le composant historique lisait l'ancienne table EVENT,
    // supprimée au profit de BUSINESS_EVENT. Sera rebranché ultérieurement
    // sur une lecture du journal métier.

    // --- i18n -----------------------------------------------------------------
    // Le pivot (#model, langue 'fr') porte l'identité du template, la Généralité
    // et le Modèle de données — communs à toutes les langues. Seuls Sujet et
    // Corps sont traduits : ils sont édités sur le modèle de la LANGUE ACTIVE.
    // #langModels : langage -> modèle (pivot pour 'fr', traduction sinon).
    // #langDirty  : langage -> tracker dirty propre à cette langue.
    readonly #pivotLangage = "fr";
    #activeLangage = "fr";
    #langModels = new Map<string, ITemplateModel>();
    #langDirty = new Map<string, DirtyTracker<ITemplateModel>>();
    // Traductions conservées lors d'un import qui a remplacé le pivot :
    // probablement obsolètes, signalées dans la barre des langues jusqu'à
    // la prochaine traduction.
    #staleLangages = new Set<string>();

    readonly #model: ITemplateModel | undefined;
    public Titre: string | undefined;
    public OnDispose = new Delegate();

    constructor(options: ITemplateEditorOptions) {
        if (options.jqMainContainer != null)
            this.MainContainer = options.jqMainContainer;

        if (options.onDispose != null)
            this.OnDispose.Bind(options.onDispose);

        this.Titre = options.titre;
        this.#dirtyTrack = new MakeDirty().Track(options.model);
        this.#model = this.#dirtyTrack.toJson()

        // Construit la table des langues : le pivot en 'fr' + ses traductions.
        this.#initLangModels(options.model);
    }

    #initLangModels = (pivot: ITemplateModel) => {
        this.#langModels.set(this.#pivotLangage, pivot);
        this.#langDirty.set(this.#pivotLangage, this.#dirtyTrack);

        (pivot.Translations ?? []).forEach(tr => {
            this.#langModels.set(tr.Langage, tr);
            this.#langDirty.set(tr.Langage, new MakeDirty().Track(tr));
        });
    }

    /** Tracker dirty de la langue actuellement affichée. */
    get #activeDirty() {
        return this.#langDirty.get(this.#activeLangage)!;
    }

    public get IsDirty() {
        // Dirty si l'une des langues a changé (pivot ou traduction).
        for (const tracker of this.#langDirty.values())
            if (tracker.isDirty())
                return true;
        return false;
    }

    public Init = async () => {
        this.#initTypeEditor();
        await this.#initStack();
        this.#buildTitre().then();
        await this.#buildHtml();
        this.#buildToolbar().then();
    }

    #initTypeEditor = () => {
        // État courant (et non l'instantané #model) : un import peut changer le type.
        switch (this.#dirtyTrack.proxy.TypeContent) {
            case 0:
                this.#typeEditor = "plaintext";
                break;
            case 1:
                this.#typeEditor = "html";
                break;
            default:
                this.#typeEditor = "plaintext";
        }
    }

    #initStack = async () => {
        this.#componentStack.Use(this.#generaliteSlot);
        this.#componentStack.Use(this.#codeSlot);
        this.#componentStack.Use(this.#modelSlot);
        this.#componentStack.Use(this.#previewSlot);
        this.#componentStack.Use(this.#sujetSlot);
        this.#componentStack.Use(this.#contractSlot);
    }

    #buildHtml = async () => {
        // Container principal
        this.#jqContainer = $("<div></div>")
            .css({
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                overfow: "hidden"
            })
            .appendTo(this.MainContainer);

        this.#buildLangBar();
        await this.#buildTabHtml();


        // Container pour le menu du bas.
        this.#jqToolbarContainer = $("<div></div>")
            .css({width: "100%"})
            .appendTo(this.#jqContainer);
    }

    // --- Barre des langues ----------------------------------------------------
    // Rangée [ FR ] [ EN ] ... [ + ] au-dessus des onglets de contenu. Cliquer
    // une langue bascule l'édition Sujet/Corps sur cette langue. '+' ouvre le
    // dialogue d'ajout. Le pivot 'fr' n'est jamais supprimable.
    #buildLangBar = () => {
        this.#jqLangBar = $("<div></div>")
            .css({display: "flex", gap: "4px", alignItems: "center", padding: "4px 0"})
            .appendTo(this.#jqContainer);

        this.#renderLangBar();
    }

    #renderLangBar = () => {
        this.#jqLangBar.empty();

        // Pivot d'abord, puis traductions dans l'ordre alphabétique de langue.
        const langs = this.#sortedLangages();

        langs.forEach(lang => {
            const active = lang === this.#activeLangage;
            const stale = this.#staleLangages.has(lang);
            $("<button></button>")
                .addClass("k-button k-button-sm k-rounded-md")
                .addClass(active ? "k-button-solid k-button-solid-primary" : "k-button-outline k-button-outline-base")
                .text(lang.toUpperCase() + (stale ? " *" : ""))
                .attr("title", stale ? "Probablement obsolète depuis le dernier import : relancer « Traduire »" : null)
                .on("click", () => this.#switchLangage(lang))
                .appendTo(this.#jqLangBar);
        });

        // Bouton "+" d'ajout de langue.
        $("<button></button>")
            .addClass("k-button k-button-sm k-rounded-md k-button-outline k-button-outline-base")
            .attr("title", "Ajouter une langue")
            .text("+")
            .on("click", () => this.#promptAddLangage())
            .appendTo(this.#jqLangBar);
    }

    /** Langues du template : pivot d'abord, puis traductions par ordre alphabétique. */
    #sortedLangages = (): Array<string> =>
        Array.from(this.#langModels.keys())
            .sort((a, b) => a === this.#pivotLangage ? -1 : b === this.#pivotLangage ? 1 : a.localeCompare(b));

    #switchLangage = (lang: string) => {
        if (lang === this.#activeLangage)
            return;

        this.#activeLangage = lang;
        this.#renderLangBar();

        // Recharge les seuls onglets dépendant de la langue (Sujet + Code) s'ils
        // sont actuellement affichés. Généralité/Modèle restent sur le pivot.
        const index = this.#kTabs ? $(this.#kTabs.select()).index() : 0;
        if (index === 1 || index === 2)
            this.#renderTab(index);
    }

    #buildTabHtml = async () => {
        const t = $("<div></div>")
            .css({width: "100%", height: "100%"})
            .appendTo(this.#jqContainer);

        const ul = $("<ul></ul>")
            .appendTo(t)
            .append($("<li></li>").text("Généralité"));

        ul.append($("<li></li>").text("Sujet"));
        ul.append($("<li></li>").text("Code " + this.#typeEditor.toUpperCase()));
        ul.append($("<li></li>").text("Modèle"));
        ul.append($("<li></li>").text("Prévisualisation"));
        ul.append($("<li></li>").text("PDF"));
        ul.append($("<li></li>").text("Notifications"));
        ul.append($("<li></li>").text("Contrat d'appel"));

        this.#tabDivs.set("generalite", $("<div circ-id='generalite'></div>").appendTo(t));
        this.#tabDivs.set("sujet", $("<div circ-id='sujet'></div>").appendTo(t));
        this.#tabDivs.set("code", $("<div circ-id='code'></div>").appendTo(t));
        this.#tabDivs.set("modele", $("<div circ-id='modele'></div>").appendTo(t));
        this.#tabDivs.set("preview", $("<div circ-id='preview'></div>").appendTo(t));
        this.#tabDivs.set("pdf", $("<div circ-id='pdf'></div>").appendTo(t));
        this.#tabDivs.set("notifications", $("<div circ-id='notifications'></div>").appendTo(t));
        this.#tabDivs.set("contrat", $("<div circ-id='contrat'></div>").appendTo(t));

        this.#jqCodeTabItem = ul.children().eq(2);

        this.#tabDivs.forEach((div, key) => {
            div.css({
                height: "100%",
            });
        })

        this.#kTabs = t.kendoTabStrip({
            animation: false,
            size: "small",
            select: (e: kendo.ui.TabStripSelectEvent) => {
                this.#renderTab($(e.item).index());
            }
        }).data("kendoTabStrip");

        this.#kTabs.select(0);
    }

    // Construit le contenu d'un onglet. Sujet (1) et Code (2) dépendent de la
    // LANGUE ACTIVE ; les autres restent sur le pivot.
    #renderTab = (index: number) => {
        switch (index) {
            case 0:
                this.#generaliteSlot.Reset(() => new TemplateGeneraliteEditor({
                    jqMainContainer: this.#tabDivs.get("generalite"),
                    model: this.#dirtyTrack.proxy
                })).Init();
                break;
            case 1:
                this.#sujetSlot.Reset(() => new TemplateSujetEditor({
                    jqMainContainer: this.#tabDivs.get("sujet"),
                    model: this.#activeDirty.proxy
                })).Init();
                break;
            case 2:
                this.#codeSlot.Reset(() => new MonacoEditorComponent({
                    jqMainContainer: this.#tabDivs.get("code"),
                    model: this.#activeDirty.proxy.BodyTemplate,
                    language: this.#typeEditor,
                    onChange: async (value) => {
                        this.#activeDirty.proxy.BodyTemplate = value
                    }
                })).Init();
                break;
            case 3:
                this.#modelSlot.Reset(() => new MonacoEditorComponent({
                    jqMainContainer: this.#tabDivs.get("modele"),
                    language: "json",
                    model: this.#dirtyTrack.proxy.Datas.length > 0 ? this.#dirtyTrack.proxy.Datas[0].Code : "{}",
                    onChange: async (value) => {
                        if (!this.#dirtyTrack.proxy.Datas.length)
                            this.#dirtyTrack.proxy.Datas.push({});

                        this.#dirtyTrack.proxy.Datas[0].Code = value
                    }
                })).Init();
                break;
            case 4:
                if (!this.#dirtyTrack.proxy.Datas.length){
                    kendo.alert("Veuillez ajouter un modèle de données avant de visualiser le rendu");
                    return;
                }
                this.#previewSlot.Reset(() => new TemplatePreview({
                    jqMainContainer: this.#tabDivs.get("preview"),
                    // Prévisualisation de la langue active (Sujet/Corps), avec le
                    // modèle de données du pivot.
                    model: this.#buildPreviewModel()
                })).Init();
                break;
            case 5:
                kendo.alert("Patience, ça arrive !");
                break;
            case 6:
                // Onglet Notifications — historique BUSINESS_EVENT
                // (à rebrancher).
                this.#tabDivs.get("notifications").empty().append(
                    $("<p></p>").css({padding: "1em"}).text("Notifications — bientôt disponible.")
                );
                break;
            case 7:
                // Contrat d'appel : documenté sur l'état courant du pivot (Id,
                // expéditeur, modèle de test) et les langues disponibles.
                this.#contractSlot.Reset(() => new TemplateContractEditor({
                    jqMainContainer: this.#tabDivs.get("contrat"),
                    model: this.#dirtyTrack.toJson(),
                    langages: this.#sortedLangages()
                })).Init();
                break;
        }
    }

    // Modèle de prévisualisation : Sujet/Corps de la langue active, mais Datas
    // (modèle de test JSON) portés par le pivot.
    #buildPreviewModel = (): ITemplateModel => {
        const active = this.#activeDirty.toJson();
        const pivot = this.#dirtyTrack.toJson();
        return {...active, Datas: pivot.Datas} as ITemplateModel;
    }

    // --- Ajout d'une langue ---------------------------------------------------
    // Dialogue minimal : saisie d'un code langue ISO 2 lettres ≠ 'fr' et non
    // déjà présent. La traduction est PRÉ-REMPLIE depuis le pivot (Sujet/Corps).
    #promptAddLangage = async () => {
        const code = await this.#askLangageCode();
        if (!code)
            return;

        const lang = code.trim().toLowerCase();
        if (!/^[a-z]{2}$/.test(lang)) {
            NotificationHelper.Instance.error("Le code langue doit faire 2 lettres (ex: en, de, es).");
            return;
        }
        if (this.#langModels.has(lang)) {
            NotificationHelper.Instance.error(`La langue '${lang}' existe déjà pour ce template.`);
            return;
        }

        const pivot = this.#dirtyTrack.toJson();
        // Traduction pré-remplie depuis le pivot : on copie Sujet/Corps, on
        // rattache au pivot (FkParent) et on neutralise le Primarykey pour que
        // le back en génère un nouveau à l'upsert.
        const translation: ITemplateModel = {
            ...pivot,
            Primarykey: String.GetEmptyGuid,
            Langage: lang,
            FkParent: pivot.Primarykey,
            Translations: undefined,
            Datas: undefined
        };

        this.#langModels.set(lang, translation);
        const tracker = new MakeDirty().Track(translation);
        // On écrit Langage VIA LE PROXY pour marquer le tracker dirty : sans
        // mutation après Track(), isDirty() resterait faux et #update sauterait
        // cette nouvelle langue (jamais persistée).
        tracker.proxy.Langage = lang;
        this.#langDirty.set(lang, tracker);

        this.#switchLangage(lang);
        this.#renderLangBar();
    }

    #askLangageCode = (): Promise<string | null> => {
        return new Promise((resolve) => {
            kendo.prompt("Code de la nouvelle langue (ISO 2 lettres, ex: en) :", "en")
                .then((value: string) => resolve(value), () => resolve(null));
        });
    }

    // --- Traduction IA --------------------------------------------------------
    // Confirmation HORS loader (l'utilisateur peut réfléchir). Renvoie vrai si
    // la traduction doit être lancée.
    #confirmTranslate = async (): Promise<boolean> => {
        const cibles = Array.from(this.#langModels.keys())
            .filter(l => l !== this.#pivotLangage);

        if (cibles.length === 0) {
            kendo.alert("Ajoutez d'abord une langue (bouton +) avant de lancer une traduction.");
            return false;
        }

        return kendo.confirm(
            `La traduction va écraser le contenu (sujet + corps) des langues suivantes : ` +
            `${cibles.map(l => l.toUpperCase()).join(", ")}.<br/>Continuer ?`
        ).then(() => true, () => false);
    }

    // Travail réseau de la traduction (sous loader) : sauvegarde du FR à jour,
    // appel serveur (Claude), rechargement des onglets.
    #runTranslate = async (): Promise<void> => {
        // On enregistre d'abord les modifications en cours (le serveur traduit
        // le FR persisté). #update gère pivot + traductions et invalide le cache.
        if (this.IsDirty)
            await this.#update();

        const pivotPk = this.#dirtyTrack.toJson().Primarykey;
        const group = await TemplateTranslate.Translate(pivotPk);
        if (!group)
            return;

        this.#reloadGroup(group);
        NotificationHelper.Instance.info("Traduction réussie.");
    }

    // Reconstruit les modèles/trackers de langue depuis un groupe rechargé
    // (après traduction serveur) et rafraîchit l'onglet courant.
    #reloadGroup = (group: Array<ITemplateModel>) => {
        const pivot = group.find(x => !x.FkParent) ?? group[0];

        // Met à jour le pivot en place (conserve l'identité du tracker pivot).
        const pivotProxy = this.#dirtyTrack.proxy;
        pivotProxy.SubjectTemplate = pivot.SubjectTemplate;
        pivotProxy.BodyTemplate = pivot.BodyTemplate;
        this.#dirtyTrack.reset();

        // Reconstruit la table des traductions à partir des données fraîches.
        for (const lang of Array.from(this.#langModels.keys())) {
            if (lang === this.#pivotLangage)
                continue;
            this.#langModels.delete(lang);
            this.#langDirty.delete(lang);
        }

        group.filter(x => x.Primarykey !== pivot.Primarykey).forEach(tr => {
            this.#langModels.set(tr.Langage, tr);
            this.#langDirty.set(tr.Langage, new MakeDirty().Track(tr));
        });

        this.#staleLangages.clear();

        // Si la langue active n'existe plus, on revient au pivot.
        if (!this.#langModels.has(this.#activeLangage))
            this.#activeLangage = this.#pivotLangage;

        this.#renderLangBar();
        const index = this.#kTabs ? $(this.#kTabs.select()).index() : 0;
        this.#renderTab(index);
    }

    #buildTitre = async () => {
        this.Titre = this.#model.Primarykey == String.GetEmptyGuid || this.#model.Primarykey == null ?
            "Nouveau Template" :
            this.#model.Nom;
    }

    #buildToolbar = async () => {
        this.#jqToolbarContainer.kendoToolBar({
            size: "small",
            items: [
                {
                    type: "button",
                    text: "Enregistrer",
                    icon: "save",
                    themeColor: "success",
                    click: (e: any) => KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#update)
                },
                {
                    type: "button",
                    text:"Supprimer",
                    icon: "trash",
                    themeColor: "error",
                    click: (e: any) => KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#delete)
                },
                {
                    type: "button",
                    text: "Importer",
                    icon: "upload",
                    click: async () => this.#import()
                },
                {
                    type: "button",
                    text: "Exporter",
                    icon: "download",
                    click: () => this.#export()
                },
                {
                    // Traduction IA via Claude (côté serveur). La confirmation se
                    // fait hors loader ; seul le travail réseau (#runTranslate)
                    // est exécuté sous le loader du bouton.
                    type: "button",
                    text: "Traduire",
                    icon: "globe",
                    themeColor: "secondary",
                    click: async (e: any) => {
                        const target = e.target;
                        if (await this.#confirmTranslate())
                            KendoToolBarHandler.AsyncToolbarHandle(target, this.#runTranslate);
                    }
                }
            ]
        });
    }

    // --- Import / export d'un paquet « easymail-template/1 » ------------------
    // L'import NE PERSISTE RIEN : il remplace le contenu de l'éditeur (état
    // « modifié »), l'utilisateur prévisualise puis enregistre. La Primarykey
    // du pivot — le TemplateId connu des applications — ne change jamais.

    #import = async () => {
        const pivot = this.#dirtyTrack.toJson();
        const pkg = await PackageImportDialog.Open({
            title: `Importer dans « ${pivot.Nom} »`,
            intro: "Remplace le contenu de ce template par celui d'un paquet (produit par un agent IA ou exporté). " +
                "Rien n'est enregistré avant que vous ne cliquiez sur « Enregistrer ».",
            context: {
                mode: "update-template",
                currentId: pivot.Primarykey,
                fkProjet: pivot.FkProjet,
                existingLanguages: this.#sortedLangages().filter(l => l !== this.#pivotLangage),
            },
            describe: this.#describeImport,
            confirmText: "Remplacer le contenu",
        });
        if (!pkg)
            return;

        if (this.IsDirty && !await kendo.confirm("Ce template contient des modifications non enregistrées, qui seront écrasées. Continuer ?")
            .then(() => true, () => false))
            return;

        this.ApplyPackage(pkg);
        NotificationHelper.Instance.info("Contenu importé : vérifiez la prévisualisation puis enregistrez.");
    }

    /** Remplace le contenu de l'éditeur par celui du paquet (sans enregistrer). */
    public ApplyPackage = (pkg: IEasymailPackage) => {
        const t = pkg.template;
        const p = this.#dirtyTrack.proxy;

        // Champs facultatifs absents du paquet : valeur actuelle conservée.
        p.Nom = t.name;
        if (t.label !== undefined)
            p.Libelle = t.label;
        p.TypeContent = PackageMapper.TypeContentToInt(t.typeContent);
        p.TypeProcess = PackageMapper.TypeProcessToInt(t.typeProcess);
        p.SubjectTemplate = t.subject;
        p.BodyTemplate = t.body;
        if (t.sender) {
            p.ExpediteurEmail = t.sender.email;
            p.ExpediteurNom = t.sender.name ?? "";
        }
        const code = PackageMapper.ModelTestToCode(pkg);
        if (!p.Datas?.length)
            p.Datas = [{Nom: "Modèle de test", Code: code}];
        else
            p.Datas[0].Code = code;

        const incoming = new Set<string>();
        for (const tr of pkg.translations ?? []) {
            incoming.add(tr.language);
            let tracker = this.#langDirty.get(tr.language);
            if (!tracker) {
                // Nouvelle langue : même construction que #promptAddLangage, mais
                // Sujet/Corps écrits APRÈS Track() pour marquer le tracker dirty.
                const translation: ITemplateModel = {
                    ...this.#dirtyTrack.toJson(),
                    Primarykey: String.GetEmptyGuid,
                    Langage: tr.language,
                    FkParent: p.Primarykey,
                    SubjectTemplate: "",
                    BodyTemplate: "",
                    Translations: undefined,
                    Datas: undefined
                };
                this.#langModels.set(tr.language, translation);
                tracker = new MakeDirty().Track(translation);
                this.#langDirty.set(tr.language, tracker);
            }
            tracker.proxy.SubjectTemplate = tr.subject;
            tracker.proxy.BodyTemplate = tr.body;
            this.#staleLangages.delete(tr.language);
        }
        this.#sortedLangages()
            .filter(l => l !== this.#pivotLangage && !incoming.has(l))
            .forEach(l => this.#staleLangages.add(l));

        this.#initTypeEditor();
        this.#refreshCodeTabLabel();
        this.#renderLangBar();
        const index = this.#kTabs ? $(this.#kTabs.select()).index() : 0;
        this.#renderTab(index);
    }

    /** Résumé des remplacements, affiché dans le rapport d'import. */
    #describeImport = (pkg: IEasymailPackage): Array<string> => {
        const cur = this.#dirtyTrack.toJson();
        const t = pkg.template;
        const lines: Array<string> = [];
        const change = (label: string, before: unknown, after: unknown) => {
            if ((before ?? "") !== (after ?? ""))
                lines.push(`${label} : « ${before ?? ""} » → « ${after ?? ""} »`);
        };
        change("Nom", cur.Nom, t.name);
        if (t.label !== undefined)
            change("Libellé", cur.Libelle, t.label);
        change("Type de contenu", PackageMapper.TypeContentFromInt(cur.TypeContent), t.typeContent);
        change("Processus", PackageMapper.TypeProcessFromInt(cur.TypeProcess), t.typeProcess ?? "mail");
        if (t.sender)
            change("Expéditeur", cur.ExpediteurEmail, t.sender.email);
        lines.push(cur.SubjectTemplate === t.subject ? "Sujet (fr) : inchangé." : "Sujet (fr) : remplacé.");
        lines.push(cur.BodyTemplate === t.body ? "Corps (fr) : inchangé." : "Corps (fr) : remplacé.");
        lines.push(TemplateEditor.#sameJson(cur.Datas?.[0]?.Code, pkg.modelTest)
            ? "Modèle de test : inchangé."
            : "Modèle de test : remplacé.");
        (pkg.translations ?? []).forEach(tr =>
            lines.push(`${tr.language.toUpperCase()} : ${this.#langModels.has(tr.language) ? "remplacée" : "ajoutée"}.`));
        lines.push("Rien n'est enregistré avant « Enregistrer » ; l'identifiant du template ne change pas.");
        return lines;
    }

    /** Compare un JSON stocké (chaîne) à un objet, indépendamment de la mise en forme. */
    static #sameJson = (stored: string | undefined, obj: unknown): boolean => {
        try {
            return JSON.stringify(JSON.parse(stored ?? "{}")) === JSON.stringify(obj);
        } catch {
            return false;
        }
    }

    #refreshCodeTabLabel = () => {
        // Le libellé « Code HTML / PLAINTEXT » dépend du type de contenu : on
        // remplace le nœud texte sans toucher au balisage généré par Kendo.
        const label = "Code " + this.#typeEditor.toUpperCase();
        const li = this.#jqCodeTabItem?.[0];
        if (!li)
            return;
        const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
        for (let n = walker.nextNode(); n; n = walker.nextNode()) {
            if ((n.textContent ?? "").trim().startsWith("Code ")) {
                n.textContent = label;
                return;
            }
        }
    }

    #export = () => {
        if (this.IsDirty)
            NotificationHelper.Instance.warning("Export de l'état affiché, qui contient des modifications non enregistrées.");

        const translations = Array.from(this.#langDirty.entries())
            .filter(([lang]) => lang !== this.#pivotLangage)
            .map(([, tracker]) => tracker.toJson());

        PackageExportDialog.Open({
            title: `Exporter « ${this.#dirtyTrack.toJson().Nom} »`,
            translationCount: translations.length,
            build: (withTranslations) => PackageMapper.FromModels({
                pivot: this.#dirtyTrack.toJson(),
                translations: withTranslations ? translations : [],
            }),
        });
    }

    #update = async () => {
        if (!this.IsDirty)
            return;

        // Si l'onglet Sujet a été ouvert, on bloque la sauvegarde tant que
        // l'expéditeur saisi n'est pas valide (le back valide aussi en dernier
        // rempart). Onglet jamais ouvert ⇒ With renvoie undefined ⇒ on laisse passer.
        const sujetValid = this.#sujetSlot.With(editor => editor.Validate());
        if (sujetValid === false) {
            kendo.alert("L'e-mail de l'expéditeur est invalide. Corrigez-le avant d'enregistrer.");
            return;
        }

        // On enregistre chaque langue modifiée. Le pivot en premier (il porte
        // l'identité et le Modèle) puis les traductions.
        const pivotTracker = this.#langDirty.get(this.#pivotLangage)!;
        const pivotPk = pivotTracker.toJson().Primarykey;

        if (pivotTracker.isDirty()) {
            // On retire le champ transitoire Translations avant l'envoi (UI only).
            const pivotPayload = pivotTracker.toJson();
            delete pivotPayload.Translations;
            await TemplateUpsert.Put(pivotPayload);
            pivotTracker.reset();
        }

        for (const [lang, tracker] of this.#langDirty) {
            if (lang === this.#pivotLangage || !tracker.isDirty())
                continue;

            // Traduction : enregistrement silencieux (pas une ligne de liste).
            // On garantit le rattachement au pivot, puis on récupère la
            // Primarykey générée pour qu'un 2e enregistrement fasse un UPDATE
            // (et non un nouvel INSERT).
            const payload = tracker.toJson();
            payload.FkParent = pivotPk;
            const saved = await TemplateUpsert.Put(payload, true);
            if (saved)
                tracker.proxy.Primarykey = saved.Primarykey;
            tracker.reset();
        }

        // Invalide le cache du groupe côté liste : la réouverture rechargera
        // toutes les langues depuis l'API.
        OnTemplateGroupSaved.Fire(pivotPk);
    }

    #delete = async () => {

        kendo.confirm("Bien certain de vouloir supprimer ce template ?</br>Tous les objets attenants seront également détruits.").then(async (result) => {
            TemplateDeleteController.Delete(this.#dirtyTrack.toJson()).then();
        })

        this.#dirtyTrack.reset();
    }

    public Dispose = () => {
        this.#componentStack.Dispose();
        this.#jqContainer.remove();
        this.OnDispose.Fire(this);
    }
}

export interface ITemplateEditorOptions {
    jqMainContainer?: JQuery;
    titre?: string;
    model: ITemplateModel;
    onDispose?: (sender: TemplateEditor) => void
}
